// src/padplanner/LayoutPanels.jsx
//
// The layout's panels that the editor and the plan page share (S6a2): the
// Layers tab (feature layers and the two check overlays, mockup 4's toggles)
// and the read-only details of a clicked feature. The editor's own Selected
// panel edits; FeatureDetails only says.
import { SectionLabel } from "./SidePanel";
import { footprintDims, zoneRadiusFt } from "../lib/footprint-editor/geometry.js";
import { CATEGORY_LABELS } from "./templateVocabulary";
import { formatApi10 } from "./planVocabulary";
import { featureName } from "./planLayout";

export const OVERLAY_LABELS = Object.freeze({ layout: "Layout checks", redzones: "Safety redzones" });
// The plan page's pins (mockup 5's comment pins, and the landowner's findings
// since S6b2, a layer of their own). Their toggles are one section, Map pins,
// shown on both the Review tab and the Layout tab: the pins draw whichever tab
// is open, so the toggle is where the pins are discussed as well as with the
// other layers. (Until 2026-10-30 they sat under Checks, on Layout only, and
// DB could not find the landowner's on 0003's Review tab.)
export const PIN_LABELS = Object.freeze({ pins: "Comment pins", landowner: "Landowner findings" });

export function MapPinLayers({ overlays, onOverlay, className = "" }) {
  return (
    <section className={`flex flex-col gap-1 ${className}`} aria-label="Map pins" data-testid="pp-pin-layers">
      <SectionLabel>Map pins</SectionLabel>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(PIN_LABELS).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-[13px] text-fg-primary">
            <input type="checkbox" checked={overlays[k]} onChange={(e) => onOverlay({ [k]: e.target.checked })} aria-label={`Show ${label}`} />
            {k === "landowner" && <span aria-hidden="true" className="inline-block h-2.5 w-2.5 rounded-[2px] bg-[#7c3aed]" />}
            {label}
          </label>
        ))}
      </div>
    </section>
  );
}

/** `pinLayers`: the plan page's, which adds the Map pins section; the editor draws no pins. */
export function LayersTab({ editor, hidden, onToggle, overlays, onOverlay, pinLayers = false }) {
  const features = editor?.getFeatures() ?? [];
  const layers = [...new Set([...features.map((f) => f.layer), ...hidden])];
  return (
    <div className="flex flex-col gap-3">
      {layers.length ? <FeatureLayers layers={layers} features={features} hidden={hidden} onToggle={onToggle} /> : <p className="op-meta">Nothing placed yet.</p>}
      <section className="flex flex-col gap-1 border-t border-bg-border pt-2" aria-label="Check layers" data-testid="pp-check-layers">
        <SectionLabel>Checks</SectionLabel>
        {Object.entries(OVERLAY_LABELS).map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-[13px] text-fg-primary">
            <input type="checkbox" checked={overlays[k]} onChange={(e) => onOverlay({ [k]: e.target.checked })} aria-label={`Show ${label}`} />
            {label}
          </label>
        ))}
      </section>
      {pinLayers && <MapPinLayers overlays={overlays} onOverlay={onOverlay} className="border-t border-bg-border pt-2" />}
    </div>
  );
}

function FeatureLayers({ layers, features, hidden, onToggle }) {
  return (
    <ul className="flex flex-col gap-1" data-testid="pp-layers">
      {layers.map((l) => (
        <li key={l} className="flex items-center justify-between text-[13px] text-fg-primary">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!hidden.has(l)} onChange={(e) => onToggle(l, e.target.checked)} aria-label={`Show ${l}`} />
            <span className="op-mono">{l}</span>
          </label>
          <span className="op-mono text-[11px] text-fg-secondary">{features.filter((f) => f.layer === l).length}</span>
        </li>
      ))}
    </ul>
  );
}

const fmt = (v, digits) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? "—" : Number(Number(v).toFixed(digits)).toLocaleString("en-US"));

/** A clicked feature, read-only: what it is, its size and turn, its layer, label and well. */
export function FeatureDetails({ feature: f, templatesById, wells = [], onClose }) {
  const t = f.template_id ? templatesById.get(f.template_id) ?? null : null;
  const name = featureName(f, templatesById);
  const dims = f.kind === "footprint" ? footprintDims(f, t) : null;
  const well = f.well_api10 ? wells.find((w) => w.api10 === f.well_api10) ?? null : null;
  const rows = [
    ...(dims ? [["Size", `${fmt(dims.lengthFt, 2)} × ${fmt(dims.widthFt, 2)} ft`], ["Rotation", `${fmt(f.rotation_deg, 1)}°`]] : []),
    ...(f.kind === "zone" ? [["Radius", `${fmt(zoneRadiusFt(f, t), 1)} ft`]] : []),
    ["Layer", f.layer],
    ["Label", f.label || "—"],
    ...(f.kind === "footprint" || f.kind === "zone"
      ? [["Wellhead", f.well_api10 ? `${well?.label ? `${well.label} · ` : ""}${formatApi10(f.well_api10)}` : "None"]] : []),
  ];
  return (
    <div className="flex flex-col gap-2.5 rounded-md border border-bg-border bg-bg/95 p-3 shadow-lg" data-testid="pp-feature-details">
      <div className="flex items-center justify-between">
        <span className="op-label uppercase">Selected</span>
        {onClose && <button type="button" className="op-meta hover:text-fg-primary" onClick={onClose} aria-label="Close details">Close</button>}
      </div>
      <div>
        <p className="text-[14px] font-semibold text-fg-primary" data-testid="pp-feature-name">{name}</p>
        <p className="op-meta">{t ? `${CATEGORY_LABELS[t.category] ?? t.category ?? ""}` : "Freeform"}</p>
      </div>
      <dl className="grid grid-cols-[76px_1fr] gap-x-3 gap-y-1 text-[13px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="op-label uppercase">{k}</dt>
            <dd className="text-fg-primary" data-testid={`pp-feature-${k.toLowerCase()}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
